import React from 'react';
import { Segment, Form, Header, Button } from 'semantic-ui-react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import download from 'downloadjs';
import { fetchStudyCycle, fetchStudyYears, fetchFaculties } from '../Lookups/util';
import { extractFileNameFromHeaders } from '../DegreeDocumentPrint/util/util';
import AccessDataTable from './AccessDataTable';
import StudentFiltersForm from './StudentFiltersForm';
import StaffFiltersForm from './StaffFiltersForm';

axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;

const USER_TYPES = {
    STUDENTS: 'students',
    STAFF: 'staff'
};

const USER_TYPE_OPTIONS = [
    { key: USER_TYPES.STUDENTS, value: USER_TYPES.STUDENTS, text: 'Studenti' },
    { key: USER_TYPES.STAFF, value: USER_TYPES.STAFF, text: 'Nastavno osoblje' }
];

const INITIAL_STATE = {
    data: [],
    selectedData: [],
    studyCycles: [],
    faculties: [],
    studyProgramBasics: [],
    studyProgramBasicsAll: [],
    studyPrograms: [],
    studyYears: [],
    faculty: '',
    studyCycle: '',
    studyProgramBasic: '',
    studyProgram: '',
    studyYear: '',
    onlyGraduates: false,
    studyCycleErr: false,
    showData: false,
    userType: USER_TYPES.STUDENTS,
    role: ''
};

export default class AccessData extends React.Component {
    constructor(props) {
        super(props);
        this.state = INITIAL_STATE;

        this.fetchData = this.fetchData.bind(this);
        this.getStudents = this.getStudents.bind(this);
        this.getStaff = this.getStaff.bind(this);
        this.onDataChange = this.onDataChange.bind(this);
        this.selectAll = this.selectAll.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.generateAccessData = this.generateAccessData.bind(this);
        this.fetchStudyProgramsForFaculty = this.fetchStudyProgramsForFaculty.bind(this);
    }

    componentDidMount() {
        this.loadInitialData();
    }

    loadInitialData() {
        axios.all([fetchStudyCycle(), fetchStudyYears(), fetchFaculties()])
            .then(axios.spread((sCyclesRes, studyYearsRes, facultiesRes) => {
                const studyCycles = sCyclesRes.data.map(el => ({
                    ...el,
                    key: el.id,
                    value: el.id,
                    text: el.displayName
                }));

                const studyYears = studyYearsRes.data.map(el => ({
                    ...el,
                    key: el.id,
                    value: el.id,
                    text: el.name
                }));

                const faculties = facultiesRes.data.map(el => ({
                    ...el,
                    key: el.id,
                    value: el.id,
                    text: el.name
                }));

                this.setState({ studyCycles, studyYears, faculties });
            }))
            .catch((error) => {
                toast('Greška prilikom učitavanja podataka.');
            });
    }

    fetchStudyProgramsForFaculty(facultyId) {
        if (!facultyId) {
            this.setState({ studyPrograms: [] });
            return;
        }

        axios.post(`study-programs/basic/${facultyId}`, {})
            .then(res => {
                if (!res.data || res.data.length === 0) {
                    this.setState({ studyPrograms: [] });
                    return;
                }

                const allPrograms = res.data.map(el => {
                    const studyCyclesText = el.studyProgramBasic.studyCycles && el.studyProgramBasic.studyCycles.length
                        ? ' | ' + el.studyProgramBasic.studyCycles.map(sc => sc.displayName).join(', ')
                        : '';

                    return {
                        key: el.studyProgramBasic.id,
                        value: el.studyProgramBasic.id,
                        text: el.studyProgramBasic.name + (el.studyProgramBasic.comment ? ' (' + el.studyProgramBasic.comment + ')' : ''),
                        description: studyCyclesText
                    };
                });

                // Remove duplicates
                const uniquePrograms = [];
                const seen = new Set();
                allPrograms.forEach(p => {
                    if (!seen.has(p.value)) {
                        seen.add(p.value);
                        uniquePrograms.push(p);
                    }
                });

                this.setState({ studyPrograms: uniquePrograms });
            })
            .catch(err => {
                this.setState({ studyPrograms: [] });
                toast('Greška prilikom učitavanja studijskih programa.');
            });
    }

    handleInputChange(event, { name, value }) {
        if (name === 'faculty') {
            this.setState({
                [name]: value,
                studyProgram: '',
                studyPrograms: [],
                studyProgramBasics: [],
                studyProgramBasicsAll: [],
                showData: false,
                data: [],
                selectedData: []
            }, () => {
                this.fetchStudyProgramsForFaculty(value);
            });
        } else if (name === 'userType') {
            // Hide table when user type changes (student vs staff)
            this.setState({
                [name]: value,
                showData: false,
                data: [],
                selectedData: []
            });
        } else if (name === 'onlyGraduates') {
            // Toggle graduates checkbox and clear study year if checked
            this.setState({
                [name]: !this.state.onlyGraduates,
                studyYear: !this.state.onlyGraduates ? '' : this.state.studyYear
            });
        } else {
            // Handle all other form inputs
            this.setState({ [name]: value });
        }
    }

    onDataChange(row) {
        const isSelected = this.state.selectedData.findIndex(el => el.id === row.id) > -1;

        if (isSelected) {
            // Remove from selection
            const newSelectedData = this.state.selectedData.filter(el => el.id !== row.id);
            this.setState({ selectedData: newSelectedData });
        } else {
            // Add to selection
            this.setState({ selectedData: [...this.state.selectedData, row] });
        }
    }

    selectAll() {
        const { data, selectedData } = this.state;
        const isAllSelected = selectedData.length === data.length;

        this.setState({
            selectedData: isAllSelected ? [] : data
        });
    }

    fetchData() {
        const { userType } = this.state;
        if (userType === USER_TYPES.STUDENTS) {
            this.getStudents();
        } else {
            this.getStaff();
        }
    }

    getStudents() {
        const {
            faculty,
            studyCycle,
            studyProgram,
            studyYear,
            onlyGraduates
        } = this.state;

        const requestDTO = {
            facultyId: faculty || null,
            studyCycleId: studyCycle || null,
            studyProgramId: studyProgram || null,
            studyYearId: studyYear || null,
            onlyGraduates: onlyGraduates || null,
            studentStudyProgramIds: null
        };

        axios.post('/generate-users/students/access-data/', requestDTO)
            .then(res => {
                // Sort students by: StudyCycle, StudyProgram, StudyYear, LastName, FirstName
                const sortedData = (res.data || []).sort((a, b) => {
                    const cycleCmp = (a.studyCycleName || '').localeCompare(b.studyCycleName || '');
                    if (cycleCmp !== 0) return cycleCmp;

                    const programCmp = (a.studyProgramName || '').localeCompare(b.studyProgramName || '');
                    if (programCmp !== 0) return programCmp;

                    const yearCmp = (a.studyYear || '').localeCompare(b.studyYear || '');
                    if (yearCmp !== 0) return yearCmp;

                    const lastNameCmp = (a.lastName || '').localeCompare(b.lastName || '');
                    if (lastNameCmp !== 0) return lastNameCmp;

                    return (a.firstName || '').localeCompare(b.firstName || '');
                });

                this.setState({
                    data: sortedData,
                    showData: true,
                    selectedData: []
                });
            })
            .catch(err => {
                toast('Greška prilikom preuzimanja podataka.');
                this.setState({
                    data: [],
                    showData: true,
                    selectedData: []
                });
            });
    }

    getStaff() {
        const { faculty, role } = this.state;

        const requestDTO = {
            facultyId: faculty || null,
            role: role || null
        };

        axios.post('/generate-users/employees/access-data/', requestDTO)
            .then(res => {
                // Sort staff by: LastName, FirstName
                const sortedData = (res.data || []).sort((a, b) => {
                    const lastNameCmp = (a.lastName || '').localeCompare(b.lastName || '');
                    if (lastNameCmp !== 0) return lastNameCmp;

                    return (a.firstName || '').localeCompare(b.firstName || '');
                });

                this.setState({
                    data: sortedData,
                    showData: true,
                    selectedData: []
                });
            })
            .catch(err => {
                toast('Greška prilikom preuzimanja podataka.');
                this.setState({
                    data: [],
                    showData: true,
                    selectedData: []
                });
            });
    }

    generateAccessData() {
        const { userType } = this.state;
        if (userType === USER_TYPES.STUDENTS) {
            this.generateStudentAccessData();
        } else {
            this.generateStaffAccessData();
        }
    }

    generateStudentAccessData() {
        const ids = this.state.selectedData.map(e => e.id);
        if (ids.length === 0) {
            toast('Niste odabrali nijednog korisnika.');
            return;
        }

        const {
            faculty,
            studyCycle,
            studyProgram,
            studyYear,
            onlyGraduates
        } = this.state;

        const requestDTO = {
            facultyId: faculty || null,
            studyCycleId: studyCycle || null,
            studyProgramId: studyProgram || null,
            studyYearId: studyYear || null,
            onlyGraduates: onlyGraduates || null,
            studentStudyProgramIds: ids
        };

        axios({
            method: 'post',
            url: '/generate-users/students/access-data/generate',
            responseType: 'blob',
            data: requestDTO
        })
            .then(res => {
                if (res.data.size === 0) {
                    toast('Nema podataka za preuzimanje.');
                    return;
                }
                const fileName = extractFileNameFromHeaders(res.headers);
                download(res.data, fileName);
                toast('Pristupni podaci su uspješno generisani.');
                this.setState({ selectedData: [] });
                // Refresh the table after successful generation
                this.getStudents();
            })
            .catch(err => {
                toast('Greška prilikom generisanja pristupnih podataka.');
            });
    }

    generateStaffAccessData() {
        const ids = this.state.selectedData.map(e => e.id);
        if (ids.length === 0) {
            toast('Niste odabrali nijednog korisnika.');
            return;
        }

        const { faculty, role } = this.state;

        const requestDTO = {
            facultyId: faculty || null,
            role: role || null,
            organizationalUnitEmployeeIds: ids
        };

        axios({
            method: 'post',
            url: '/generate-users/employees/access-data/generate',
            responseType: 'blob',
            data: requestDTO
        })
            .then(res => {
                if (res.data.size === 0) {
                    toast('Nema podataka za preuzimanje.');
                    return;
                }
                const fileName = extractFileNameFromHeaders(res.headers);
                download(res.data, fileName);
                toast('Pristupni podaci su uspješno generisani.');
                this.setState({ selectedData: [] });
                // Refresh the table after successful generation
                this.getStaff();
            })
            .catch(err => {
                toast('Greška prilikom generisanja pristupnih podataka.');
            });
    }

    isFormValid() {
        const { faculty, userType, role } = this.state;
        return faculty && userType && (userType !== USER_TYPES.STAFF || role);
    }

    getButtonText() {
        return this.state.userType === USER_TYPES.STUDENTS
            ? 'Prikaži studente'
            : 'Prikaži nastavno osoblje';
    }

    render() {
        const {
            studyCycle,
            studyCycles,
            faculties,
            studyPrograms,
            studyYears,
            userType,
            faculty
        } = this.state;

        return (
            <div>
                <Header as="h1">Pristupni podaci</Header>
                <Segment.Group raised>
                    <Segment>
                        <Header as="h3">Korisnici</Header>
                    </Segment>
                    <Segment clearing>
                        <Form>
                            <Form.Group widths="three">
                                <Form.Field required>
                                    <label htmlFor="faculty">Fakultet</label>
                                    <Form.Select
                                        id="faculty"
                                        value={faculty}
                                        onChange={this.handleInputChange}
                                        name="faculty"
                                        options={faculties}
                                        search
                                        fluid
                                        placeholder="Odaberite fakultet"
                                    />
                                </Form.Field>
                                <Form.Field required>
                                    <label htmlFor="userType">Korisnici</label>
                                    <Form.Select
                                        id="userType"
                                        value={userType}
                                        onChange={this.handleInputChange}
                                        name="userType"
                                        options={USER_TYPE_OPTIONS}
                                        fluid
                                        placeholder="Odaberite tip korisnika"
                                    />
                                </Form.Field>
                                <Form.Field />
                            </Form.Group>

                            {userType === USER_TYPES.STUDENTS && (
                                <StudentFiltersForm
                                    studyCycle={studyCycle}
                                    studyCycles={studyCycles}
                                    faculty={faculty}
                                    studyProgram={this.state.studyProgram}
                                    studyPrograms={studyPrograms}
                                    studyYear={this.state.studyYear}
                                    studyYears={studyYears}
                                    onlyGraduates={this.state.onlyGraduates}
                                    handleInputChange={this.handleInputChange}
                                />
                            )}

                            {userType === USER_TYPES.STAFF && (
                                <StaffFiltersForm
                                    faculty={faculty}
                                    role={this.state.role}
                                    handleInputChange={this.handleInputChange}
                                />
                            )}
                        </Form>

                        <br />
                        <Button
                            color="blue"
                            onClick={this.fetchData}
                            floated="right"
                            disabled={!this.isFormValid()}
                        >
                            {this.getButtonText()}
                        </Button>

                        <br />
                        {this.state.showData && (
                            <div>
                                <br /><br />
                                <AccessDataTable
                                    data={this.state.data}
                                    selectedData={this.state.selectedData}
                                    onDataChange={this.onDataChange}
                                    selectAll={this.selectAll}
                                    generateAccessData={this.generateAccessData}
                                    userType={userType}
                                />
                            </div>
                        )}
                    </Segment>

                    <ToastContainer hideProgressBar />
                </Segment.Group>
            </div>
        );
    }
}




// WEBPACK FOOTER //
// ./src/views/GenerateUsers/AccessData.js